import type { Edit, FontRef, PdfDocument } from '@pdf-ticket/pdf-core';

/**
 * Path-(b) CJK add-text support.
 *
 * When the user adds text that no existing page font can place (a fontless page,
 * or CJK glyphs absent from the page's fonts), the driver embeds a *subset* of
 * this fallback font for the new text. This is an INTENTIONAL add by the user,
 * so it is consistent with the font-preservation absolute rules: substitute
 * fonts for UNEDITED content are still never written, and the original byte
 * prefix is preserved (the embed is append-only). The driver subsets at save
 * time, so only the glyphs actually typed are embedded (a few KB), not the whole
 * font.
 *
 * The asset is a full static Noto Sans JP Regular (OFL) under public/fonts. It is
 * ~5.5MB, so we fetch it LAZILY — only on the first save that actually needs it —
 * and cache the bytes for the rest of the session.
 */
const FONT_URL = `${import.meta.env.BASE_URL}fonts/NotoSansJP-Regular.ttf`;

let cached: Promise<Uint8Array> | null = null;

/** Fetch (once) and return the CJK fallback font bytes. Retries on a later call if the fetch fails. */
export function loadCjkFallbackFont(): Promise<Uint8Array> {
  if (!cached) {
    cached = fetch(FONT_URL)
      .then((res) => {
        if (!res.ok) throw new Error(`CJK fallback font fetch failed: HTTP ${res.status}`);
        return res.arrayBuffer();
      })
      .then((buf) => new Uint8Array(buf))
      .catch((err) => {
        cached = null; // allow a retry on the next save
        throw err;
      });
  }
  return cached;
}

/**
 * A FontRef that matches no real page font (PDF object 0 never exists), used when
 * placing text on a page that has no usable font. It carries no encoding/embed
 * info, so the driver cannot resolve it to an existing font and takes the
 * fallback-embed path instead.
 */
export const EMBED_FALLBACK_FONT_REF: FontRef = {
  originalRef: 0 as FontRef['originalRef'],
  postScriptName: 'EmbedFallback',
  baseFont: 'EmbedFallback',
  subset: false,
  embedded: false,
  encoding: null,
};

/** True if any code unit is outside ASCII (> U+007F) — i.e. likely needs an embedded font. */
function hasNonAscii(text: string): boolean {
  for (let i = 0; i < text.length; i++) {
    if (text.charCodeAt(i) > 0x7f) return true;
  }
  return false;
}

/**
 * Whether a save *might* need the CJK fallback font, used only to decide whether
 * to pay the 5.5MB fetch. The driver makes the real per-edit decision and only
 * embeds when an edit genuinely defers (and dedups to one subset); passing the
 * font when it turns out unneeded adds zero bytes. So this gate errs toward
 * fetching when in doubt, while skipping the common case (ASCII text on a page
 * that already has fonts).
 */
export function mayNeedCjkFallback(doc: PdfDocument, edits: readonly Edit[]): boolean {
  return edits.some((e) => {
    if (e.kind !== 'add-text') return false;
    const page = doc.pages[e.page];
    if (!page || page.fonts.length === 0) return true; // fontless target page: any text needs embedding
    return hasNonAscii(e.text); // non-Latin text likely needs an embedded font
  });
}
